import {
	Component,
	computed,
	effect,
	inject,
	input,
	InputSignal,
	output,
	Signal,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chart, Options, Series, SeriesColumnOptions } from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { BarChartSettings } from '../../models/barChartSettings';
import { IBarChartParam, IBarChartParamValue } from '../../models/barChartParams';
import { IBarChartSettingsValue } from '../../models/barChartSettingsValue';
import {
	getGroupedBarChartCellTotal,
	getGroupedBarChartSubValue,
	IGroupedBarChartCategory,
	IGroupedBarChartData,
	IGroupedBarChartSeries,
} from '../../models/groupedBarChart';
import { SKELETON_THEME } from '../../models/skeletonTheme';
import { BarChartSettingsModalComponent } from '../../modals/bar-chart-settings-modal/bar-chart-settings-modal.component';
import { LoaderComponent } from '../loader/loader.component';
import { SessionService } from '../../services/session.service';
import { SnackBarService } from '../../services/snack-bar.service';
import { Utils } from '../../services/utils.service';
import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

@Component({
	selector: 'app-ph-grouped-bar-chart',
	templateUrl: './ph-grouped-bar-chart.component.html',
	styleUrls: ['./ph-grouped-bar-chart.component.scss'],
	imports: [
		TranslateModule,
		SHARED_MODULES,
		HighchartsChartComponent,
		LoaderComponent,
		PhIconComponent,
		PhIconWrapperComponent,
		NgxSkeletonLoaderComponent
	]
})
export class PhGroupedBarChartComponent {

	private chart: Chart = null;

	readonly data: InputSignal<IGroupedBarChartData> = input<IGroupedBarChartData>(null);
	readonly loading: InputSignal<boolean> = input<boolean>(false);
	readonly params: InputSignal<Array<IBarChartParam>> = input<Array<IBarChartParam>>([]);
	readonly settingsKey: InputSignal<string> = input<string>('');
	readonly hideOptions: InputSignal<boolean> = input<boolean>(false);
	readonly valueSuffix: InputSignal<string> = input<string>('%');
	readonly downloading: InputSignal<boolean> = input<boolean>(false);

	readonly settingsUpdate = output<IBarChartSettingsValue>();
	readonly download = output<Array<string>>();

	protected readonly skeletonTitleTheme: Record<string, string>;
	protected readonly skeletonLegendTheme: Record<string, string>;
	protected readonly skeletonChartTheme: Record<string, string>;
	protected readonly options: Signal<Options> = computed(() => this.buildOptions());

	private readonly dialog: MatDialog = inject(MatDialog);
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly sessionService: SessionService = inject(SessionService);
	private readonly snackBarService: SnackBarService = inject(SnackBarService);

	constructor() {
		this.skeletonTitleTheme = {
			...SKELETON_THEME,
			width: '280px',
			height: '16px',
			borderRadius: '8px',
			margin: '0',
		};
		this.skeletonLegendTheme = {
			...SKELETON_THEME,
			width: '96px',
			height: '14px',
			borderRadius: '8px',
			margin: '0',
		};
		this.skeletonChartTheme = {
			...SKELETON_THEME,
			width: '100%',
			height: '100%',
			borderRadius: '8px',
			margin: '0',
		};

		effect(() => {
			const options = this.options();
			if (!options) {
				this.chart = null;
				return;
			}
			this.chart?.update(options, true, true);
		});
	}

	onParamChange(param: IBarChartParam, value: IBarChartParamValue): void {
		param.control.setValue(value);
	}

	onChartInstance(chart: Chart): void {
		this.chart = chart;
	}

	onDownload(): void {
		if (this.downloading()) return;

		this.download.emit(
			(this.chart?.series || [])
				.filter((series: Series) => series.visible)
				.map((series: Series) => series.options.id)
				.filter(Boolean)
		);
	}

	openSettingsModal(): void {
		const key = this.settingsKey();
		const saved = this.sessionService.settings[key];
		const metrics = this.params().find((param: IBarChartParam) => param.key === 'order_metric').values;
		const settings = new BarChartSettings({
			...saved,
			defaultMetric: saved?.defaultMetric || metrics[0]?.key,
		});

		const dialogRef: MatDialogRef<BarChartSettingsModalComponent> = this.dialog.open(
			BarChartSettingsModalComponent,
			{
				width: '1200px',
				autoFocus: false,
				backdropClass: 'back-drop',
				panelClass: 'scrollable-content',
				data: {
					chartName: this.data().title,
					settings,
					previewType: 'grouped',
					metrics,
				},
			}
		);

		dialogRef.afterClosed().subscribe((result: IBarChartSettingsValue) => {
			if (!result) return;

			const needRequest: boolean =
				result.count !== settings.count || result.defaultMetric !== settings.defaultMetric;

			this.sessionService.defaults.settings[key] = {
				...this.sessionService.settings[key],
				...result,
			};
			this.sessionService.updateSettings$().subscribe({
				next: () => {
					if (needRequest) this.settingsUpdate.emit(result);
				},
				error: () => {
					this.snackBarService.setErrorNotification();
				},
			});
		});
	}

	private static readonly SUB_SEGMENT_MIN_LABEL_HEIGHT_PX = 11;
	private static readonly SUB_SEGMENT_LABEL_FONT_SIZE = '9px';
	private static readonly SUB_SEGMENT_LABEL_Y_OFFSET_PX = 2;
	private static readonly SUB_SEGMENT_LABEL_HORIZONTAL_INSET_PX = 2;
	private static readonly SPLIT_CATEGORY_SERIES_CLASS = 'ph-grouped-bar-chart-split-category';
	private getSubSegmentShare(
		cell: IGroupedBarChartSeries['values'][number],
		subKey: string,
		columnTotal: number,
		valueSuffix: string
	): number {
		const sub = getGroupedBarChartSubValue(cell, subKey);
		if (!sub) {
			return 0;
		}
		if (valueSuffix === '%') {
			return Math.min(Math.max(sub / 100, 0), 1);
		}
		return columnTotal ? Math.min(sub, columnTotal) / columnTotal : 0;
	}

	private shouldShowSubSegmentDetails(
		subShare: number,
		subValue: number,
		columnTotal: number,
		valueSuffix: string
	): boolean {
		if (!subShare || !columnTotal) {
			return false;
		}
		if (valueSuffix === '%') {
			return subValue >= 5 && subShare >= 0.08;
		}
		return subValue >= 2 && subShare >= 0.1;
	}

	private buildSplitColumnPointColor(
		groupColor: string,
		subColor: string,
		share: number,
		showDetails: boolean
	): string | Highcharts.GradientColorObject {
		if (!showDetails || !share) {
			return groupColor;
		}
		if (share >= 1) {
			return subColor;
		}
		const divider = Math.min(share + 0.004, 1);
		return {
			linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
			stops: [
				[0, subColor],
				[share, subColor],
				[share, '#ffffff'],
				[divider, '#ffffff'],
				[divider, groupColor],
				[1, groupColor],
			],
		};
	}

	private isSplitCategorySeries(series: Series): boolean {
		return series.options.className === PhGroupedBarChartComponent.SPLIT_CATEGORY_SERIES_CLASS;
	}

	private readSplitPointCustom(point: Highcharts.Point): {
		showSubLabel?: boolean;
		showSeparator?: boolean;
		subShare?: number;
		labelValue?: number;
	} | undefined {
		const withCustom = point as Highcharts.Point & {
			custom?: {
				showSubLabel?: boolean;
				showSeparator?: boolean;
				subShare?: number;
				labelValue?: number;
			};
		};
		return (
			withCustom.custom ??
			(point.options as {
				custom?: {
					showSubLabel?: boolean;
					showSeparator?: boolean;
					subShare?: number;
					labelValue?: number;
				};
			}).custom
		);
	}

	private drawSplitCategoryOverlays(
		chart: Chart,
		formatColumnLabel: (value: number) => string | null
	): void {
		const chartWithGroup = chart as Chart & { splitCategoryOverlayGroup?: Highcharts.SVGElement };
		if (chartWithGroup.splitCategoryOverlayGroup) {
			chartWithGroup.splitCategoryOverlayGroup.destroy();
		}

		chartWithGroup.splitCategoryOverlayGroup = chart.renderer.g('split-category-overlays').add();

		const plotLeft = chart.plotLeft ?? 0;
		const plotTop = chart.plotTop ?? 0;

		for (const series of chart.series) {
			if (!this.isSplitCategorySeries(series)) {
				continue;
			}
			for (const point of series.points) {
				const custom = this.readSplitPointCustom(point);
				if (!point.shapeArgs || !point.y || !custom) {
					continue;
				}

				const { x, y, width, height } = point.shapeArgs;
				const share = custom.subShare ?? 0;

				if (custom.showSeparator && share) {
					const lineX1 = plotLeft + x;
					const lineX2 = plotLeft + x + width;
					const lineY = plotTop + y + height * share + 0.5;
					chart.renderer
						.path([
							['M', lineX1, lineY],
							['L', lineX2, lineY],
						])
						.attr({
							stroke: '#ffffff',
							'stroke-width': 1,
						})
						.add(chartWithGroup.splitCategoryOverlayGroup);
				}

				if (!custom.showSubLabel || !share) {
					continue;
				}

				const subBandHeight = height * share;
				if (
					subBandHeight > 0 &&
					subBandHeight < PhGroupedBarChartComponent.SUB_SEGMENT_MIN_LABEL_HEIGHT_PX
				) {
					continue;
				}

				const availableWidth =
					width - PhGroupedBarChartComponent.SUB_SEGMENT_LABEL_HORIZONTAL_INSET_PX * 2;
				if (availableWidth <= 0) {
					continue;
				}

				const labelText = formatColumnLabel(custom.labelValue ?? 0);
				if (!labelText) {
					continue;
				}

				const centerX = plotLeft + x + width / 2;
				const centerY =
					plotTop +
					y +
					subBandHeight / 2 +
					PhGroupedBarChartComponent.SUB_SEGMENT_LABEL_Y_OFFSET_PX;

				const label = chart.renderer
					.text(labelText, centerX, centerY)
					.css({
						color: '#ffffff',
						fontSize: PhGroupedBarChartComponent.SUB_SEGMENT_LABEL_FONT_SIZE,
						fontWeight: '400',
						fontFamily: "'Roboto Condensed', sans-serif",
					})
					.attr({
						align: 'center',
						zIndex: 8,
					})
					.add(chartWithGroup.splitCategoryOverlayGroup);

				if (label.getBBox().width > availableWidth) {
					label.destroy();
				}
			}
		}

		chartWithGroup.splitCategoryOverlayGroup.toFront();
	}

	private buildOptions(): Options | null {
		const data = this.data();
		if (!data) return null;

		const items: Array<IGroupedBarChartSeries> = data.series || [];
		const groups: Array<IGroupedBarChartCategory> = data.categories || [];
		const suffix = this.valueSuffix();
		const dataMax = Math.max(
			0,
			...items.flatMap((item) => (item.values || []).map((cell) => getGroupedBarChartCellTotal(cell)))
		);
		const minYMax = suffix === '%' ? 100 : 0;
		const yMax = Math.max(minYMax, Math.ceil((dataMax + 10) / 25) * 25);
		const series: Array<SeriesColumnOptions> = [];

		const formatColumnLabel = (value: number): string | null => {
			if (!value) {
				return null;
			}
			if (suffix === '%') {
				return `${Utils.roundToFixed(value, 2)}${suffix}`;
			}
			return `${Math.round(value)}${suffix}`;
		};

		const formatSubSegmentLabel = (value: number): string | null => {
			if (!value) {
				return null;
			}
			return `${Math.round(value)}${suffix}`;
		};

		const drawSplitCategoryOverlays = (chart: Chart) =>
			this.drawSplitCategoryOverlays(chart, formatSubSegmentLabel);

		const columnTotalDataLabels = {
			enabled: true,
			inside: false,
			verticalAlign: 'bottom' as const,
			y: 2,
			crop: false,
			overflow: 'allow' as const,
			allowOverlap: false,
			style: {
				fontWeight: '400',
				textOutline: 'none',
			},
		};

		groups.forEach((group, index) => {
			const categoryName = this.translateService.instant(group.name);
			const subs = group.sub_categories || [];

			if (!subs.length) {
				series.push({
					type: 'column',
					id: group.key || group.name,
					name: categoryName,
					color: group.color,
					data: items.map((item) => getGroupedBarChartCellTotal(item.values[index])),
					dataLabels: {
						...columnTotalDataLabels,
						formatter: function () {
							return formatColumnLabel(Number(this.y));
						},
					},
				});
				return;
			}

			const sub = subs[0];
			series.push({
				type: 'column',
				id: group.key || group.name,
				name: categoryName,
				color: group.color,
				className: PhGroupedBarChartComponent.SPLIT_CATEGORY_SERIES_CLASS,
				data: items.map((item) => {
					const cell = item.values[index];
					const total = getGroupedBarChartCellTotal(cell);
					const subValue = getGroupedBarChartSubValue(cell, sub.key);
					const subShare = this.getSubSegmentShare(cell, sub.key, total, suffix);
					const showDetails = this.shouldShowSubSegmentDetails(subShare, subValue, total, suffix);
					return {
						y: total,
						color: this.buildSplitColumnPointColor(
							group.color,
							sub.color,
							subShare,
							showDetails
						),
						custom: {
							showSubLabel: showDetails,
							showSeparator: showDetails,
							subShare,
							labelValue: subValue,
						},
					};
				}),
				dataLabels: {
					...columnTotalDataLabels,
					formatter: function () {
						return formatColumnLabel(Number(this.y));
					},
				},
			});
		});

		series.forEach((item: SeriesColumnOptions, index: number) => {
			item.index = index;
			item.legendIndex = index;
		});

		const translateService = this.translateService;

		return {
			chart: {
				type: 'column',
				spacingTop: 16,
				style: {
					fontFamily: "'Roboto Condensed', sans-serif",
				},
				events: {
					render(): void {
						drawSplitCategoryOverlays(this as Chart);
					},
				},
			},
			title: {
				text: data.title,
				align: 'left',
				margin: 8,
				style: {
					fontSize: '16px',
					fontWeight: '700',
				},
			},
			credits: { enabled: false },
			exporting: { enabled: false },
			accessibility: { enabled: false },
			legend: {
				enabled: true,
				align: 'left',
				verticalAlign: 'top',
				itemDistance: 10,
				itemStyle: {
					fontWeight: '400',
				},
				itemHiddenStyle: {
					textDecoration: 'none',
				},
			},
			xAxis: {
				categories: items.map((item) => item.name),
				min: 0,
				max: Math.max(items.length - 1, 0),
				lineWidth: 0,
				tickLength: 0,
				offset: 0,
				labels: {
					y: 14,
					padding: 0,
					allowOverlap: false,
					style: {
						color: '#8b8b8b',
						fontSize: '12px',
						fontWeight: '700',
						whiteSpace: 'nowrap',
					},
				},
			},
			yAxis: {
				min: 0,
				max: yMax,
				tickInterval: suffix === '%' ? 25 : undefined,
				endOnTick: true,
				title: { text: '' },
				labels: { enabled: false },
				gridLineWidth: 0,
				lineWidth: 0,
				tickLength: 0,
			},
			tooltip: {
				shared: true,
				useHTML: true,
				formatter: function () {
					const firstPoint = this.points?.[0];
					const pointIndex = firstPoint?.index ?? 0;
					const item = items[pointIndex];
					const storeName = item?.name || firstPoint?.key || '';
					let html = `<span style="font-weight:700">${storeName}</span><br/>`;
					if (item?.planograms_count !== undefined) {
						const planogramsLabel = translateService.instant('COMMON.ENTITY.PLANOGRAMS');
						html += `${planogramsLabel}: <b>${item.planograms_count}</b><br/>`;
					}

					groups.forEach((group, catIndex) => {
						const cell = item?.values[catIndex];
						const total = getGroupedBarChartCellTotal(cell);
						const name = translateService.instant(group.name);
						const totalLabel = formatColumnLabel(total) ?? `0${suffix}`;
						let line = `<span style="color:${group.color}">\u25CF</span> ${name}: <b>${totalLabel}</b>`;

						for (const sub of group.sub_categories || []) {
							const subValue = getGroupedBarChartSubValue(cell, sub.key);
							if (!subValue) {
								continue;
							}
							const subName = sub.name.charAt(0).toLowerCase() + sub.name.slice(1);
							line += `, ${translateService.instant('CONTROL_DASHBOARD.CHART_TOOLTIP_OF_WHICH')} <span style="color:${sub.color}">\u25CF</span> ${subName}: <b>${formatColumnLabel(subValue)}</b>`;
						}

						html += `${line}<br/>`;
					});

					return html;
				},
			},
			plotOptions: {
				column: {
					pointPadding: 0.1,
					borderWidth: 0,
					dataLabels: {
						inside: false,
						allowOverlap: false,
					},
				},
			},
			series,
		};
	}
}
